import { useEffect, useState, useCallback } from "react";
import { chListCentros, chListInspeccionesEmpresa, chCrearInspeccion, chBorrarInspeccion,
  chGetInspeccion, chEditarInspeccion, chGetMeta } from "@/lib/prevelis";
import { formatFecha } from "@/lib/format";
import { toast } from "sonner";
import SuizaChecklist from "@/pages/SuizaChecklist";
import SuizaAnalisisInspeccion from "@/pages/SuizaAnalisisInspeccion";
import SuizaDocsInspeccion from "@/pages/SuizaDocsInspeccion";
import { Plus, ClipboardCheck, Building, User, Calendar, Trash2, Loader2, X, Save, ArrowLeft } from "lucide-react";

const field = "w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none text-sm";
const ESTADO_STYLE = {
  borrador: "bg-slate-100 text-slate-600", en_curso: "bg-blue-100 text-blue-700",
  finalizada: "bg-amber-100 text-amber-700", analizada: "bg-indigo-100 text-indigo-700",
  cerrada: "bg-emerald-100 text-emerald-700",
};

export default function SuizaInspecciones({ empresaId, onCount }) {
  const [centros, setCentros] = useState([]);
  const [items, setItems] = useState([]);
  const [estados, setEstados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ centro_id: "", titulo: "", fecha_inspeccion: "", tecnico: "" });
  const [saving, setSaving] = useState(false);
  const [detalle, setDetalle] = useState(null);

  const estadoLabel = useCallback((cod) => estados.find((e) => e.codigo === cod)?.es || cod, [estados]);
  const load = useCallback(() => {
    setLoading(true);
    Promise.all([chListCentros(empresaId), chListInspeccionesEmpresa(empresaId)])
      .then(([c, i]) => { setCentros(c); setItems(i); onCount?.(i.length); })
      .catch(() => {}).finally(() => setLoading(false));
  }, [empresaId, onCount]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { chGetMeta().then((m) => setEstados(m.estados_inspeccion || [])).catch(() => {}); }, []);

  const abrirNueva = () => {
    setForm({ centro_id: centros[0]?.id || "", titulo: "", fecha_inspeccion: new Date().toISOString().slice(0, 10), tecnico: "" });
    setOpen(true);
  };
  const crear = async () => {
    if (!form.centro_id) { toast.error("Selecciona un centro"); return; }
    setSaving(true);
    try { await chCrearInspeccion(form.centro_id, form); toast.success("Inspección creada"); setOpen(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo crear"); }
    finally { setSaving(false); }
  };
  const borrar = async (i) => {
    if (!window.confirm("¿Eliminar esta inspección y todos sus datos?")) return;
    try { await chBorrarInspeccion(i.id); toast.success("Inspección eliminada"); load(); }
    catch { toast.error("No se pudo eliminar"); }
  };
  const abrirDetalle = async (i) => {
    try { const full = await chGetInspeccion(i.id); setDetalle(full); }
    catch { toast.error("No se pudo abrir la inspección"); }
  };

  if (detalle) return <InspeccionDetalle inspeccion={detalle} estados={estados} onBack={() => { setDetalle(null); load(); }} />;

  return (
    <div data-testid="ch-inspecciones-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h3 className="font-heading font-semibold text-lg">Inspecciones</h3>
          <p className="text-sm text-slate-500">Cada inspección se realiza sobre un único centro. Un centro puede tener varias inspecciones a lo largo del tiempo.</p>
        </div>
        <button onClick={abrirNueva} disabled={centros.length === 0} data-testid="ch-inspeccion-nueva" className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-blue-700 text-white text-sm font-medium hover:bg-blue-800 disabled:opacity-40">
          <Plus className="w-4 h-4" /> Nueva inspección
        </button>
      </div>

      {loading ? (
        <div className="text-slate-400 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>
      ) : centros.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-8 text-center text-slate-500" data-testid="ch-inspecciones-sin-centros">
          Primero crea al menos un centro en la pestaña «Centros».
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-8 text-center text-slate-500" data-testid="ch-inspecciones-vacio">
          Aún no hay inspecciones. Crea la primera con «Nueva inspección».
        </div>
      ) : (
        <div className="space-y-3" data-testid="ch-inspecciones-lista">
          {items.map((i) => (
            <div key={i.id} data-testid={`ch-inspeccion-${i.id}`} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center shrink-0"><ClipboardCheck className="w-4 h-4" /></div>
              <button onClick={() => abrirDetalle(i)} data-testid={`ch-inspeccion-abrir-${i.id}`} className="min-w-0 flex-1 text-left">
                <div className="font-medium text-slate-900 truncate hover:text-blue-700">{i.titulo}</div>
                <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
                  <span className="inline-flex items-center gap-1"><Building className="w-3.5 h-3.5" /> {i.centro_nombre}</span>
                  {i.fecha_inspeccion && <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {i.fecha_inspeccion}</span>}
                  {i.tecnico && <span className="inline-flex items-center gap-1"><User className="w-3.5 h-3.5" /> {i.tecnico}</span>}
                  <span className="text-slate-400">Creada {formatFecha(i.created_at)}</span>
                </div>
              </button>
              <span data-testid={`ch-inspeccion-estado-${i.id}`} className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-1 rounded-full ${ESTADO_STYLE[i.estado] || "bg-slate-100 text-slate-600"}`}>
                {estadoLabel(i.estado)}
              </span>
              <button onClick={() => borrar(i)} data-testid={`ch-inspeccion-borrar-${i.id}`} className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-red-50 text-red-600"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="ch-inspeccion-dialog">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold text-lg">Nueva inspección</h3>
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div><label className="text-sm font-medium text-slate-700">Centro *</label>
                <select data-testid="ch-inspeccion-centro" value={form.centro_id} onChange={(e) => setForm((s) => ({ ...s, centro_id: e.target.value }))} className={field + " mt-1 h-11"}>
                  <option value="">— Selecciona —</option>
                  {centros.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                </select></div>
              <div><label className="text-sm font-medium text-slate-700">Título (opcional)</label>
                <input data-testid="ch-inspeccion-titulo" value={form.titulo} placeholder="Inspección · nombre del centro" onChange={(e) => setForm((s) => ({ ...s, titulo: e.target.value }))} className={field + " mt-1 h-11"} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-sm font-medium text-slate-700">Fecha de inspección</label>
                  <input type="date" data-testid="ch-inspeccion-fecha" value={form.fecha_inspeccion} onChange={(e) => setForm((s) => ({ ...s, fecha_inspeccion: e.target.value }))} className={field + " mt-1 h-11"} /></div>
                <div><label className="text-sm font-medium text-slate-700">Técnico</label>
                  <input data-testid="ch-inspeccion-tecnico" value={form.tecnico} onChange={(e) => setForm((s) => ({ ...s, tecnico: e.target.value }))} className={field + " mt-1 h-11"} /></div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setOpen(false)} className="h-11 px-4 rounded-lg border border-slate-300 font-medium hover:bg-slate-50">Cancelar</button>
              <button onClick={crear} disabled={saving} data-testid="ch-inspeccion-guardar" className="inline-flex items-center gap-2 h-11 px-5 rounded-lg bg-blue-700 text-white font-medium hover:bg-blue-800 disabled:opacity-50">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Crear
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function InspeccionDetalle({ inspeccion, estados, onBack }) {
  const [insp, setInsp] = useState(inspeccion);
  const [vista, setVista] = useState("checklist");
  const estadoLabel = (cod) => estados.find((e) => e.codigo === cod)?.es || cod;

  const cambiarEstado = async (estado) => {
    try { const r = await chEditarInspeccion(insp.id, { estado }); setInsp(r); toast.success(`Estado: ${estadoLabel(estado)}`); }
    catch { toast.error("No se pudo cambiar el estado"); }
  };

  return (
    <div data-testid="ch-inspeccion-detalle">
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-700 hover:text-blue-800 mb-4">
        <ArrowLeft className="w-4 h-4" /> Volver a inspecciones
      </button>
      <div className="bg-white border border-slate-200 rounded-xl p-5 mb-4">
        <h3 className="font-heading font-semibold text-lg">{insp.titulo}</h3>
        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1"><Building className="w-3.5 h-3.5" /> {insp.centro_nombre}</span>
          {insp.fecha_inspeccion && <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {insp.fecha_inspeccion}</span>}
          {insp.tecnico && <span className="inline-flex items-center gap-1"><User className="w-3.5 h-3.5" /> {insp.tecnico}</span>}
        </div>
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <span className="text-sm text-slate-500">Estado:</span>
          {estados.map((e) => (
            <button key={e.codigo} onClick={() => cambiarEstado(e.codigo)} data-testid={`ch-inspeccion-set-${e.codigo}`}
              className={`text-xs font-medium px-2.5 py-1 rounded-full border ${insp.estado === e.codigo ? "bg-blue-700 text-white border-blue-700" : "border-slate-300 text-slate-600 hover:bg-slate-50"}`}>
              {e.es}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1 mb-4 w-fit" data-testid="ch-inspeccion-vista">
        {[["checklist", "Checklist"], ["analisis", "Peligros y medidas (IA)"], ["documentos", "Documentos"]].map(([k, l]) => (
          <button key={k} onClick={() => setVista(k)} data-testid={`ch-inspeccion-vista-${k}`}
            className={`px-4 h-9 rounded-md text-sm font-medium transition-colors ${vista === k ? "bg-white text-blue-700 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
            {l}
          </button>
        ))}
      </div>

      {vista === "checklist" && <SuizaChecklist inspeccion={insp} onEstado={(e) => setInsp((s) => ({ ...s, estado: e }))} />}
      {vista === "analisis" && <SuizaAnalisisInspeccion inspeccion={insp} onEstado={(e) => setInsp((s) => ({ ...s, estado: e }))} />}
      {vista === "documentos" && <SuizaDocsInspeccion inspeccion={insp} />}
    </div>
  );
}
